/**
 * AQ-Cloud2 application header.
 *
 * Three-zone layout, 56px tall, sticky, z-40, 1px bottom border, full-width
 * (NOT container-constrained — content density on dual-monitor desktops is
 * the priority over a centered marketing layout).
 *
 *   [ BrandLockup | divider | Tenant switcher ]
 *   [ (search trigger hidden — not yet functional) ]
 *   [ Create ▾ | Notifications | Help | Avatar ]
 *
 * Mirrors design-mockups/shell-desktop.html and design-mockups/header-detail.html.
 */
import React from 'react'
import { Link } from 'react-router-dom'
import { HelpCircle } from 'lucide-react'
import { NotificationBell } from '@revalize/notifications/react'
import { useAuth } from '@/hooks/useAppAuth'
import { LoginButton } from '@/platform/auth/LoginButton'
import { UserMenuDropdown } from '@/components/layout/UserMenuDropdown'
import { BrandLockup } from '@/components/layout/BrandLockup'
import { TenantBadge } from './TenantBadge'
import { CreateMenu } from '@/components/layout/CreateMenu'
import { cn } from '@/lib/utils'

interface HeaderProps {
  /** Override the brand lockup (e.g. for embed/admin shells). */
  logo?: React.ReactNode
  /** Slot for mobile menu toggle injected by AppLayout. */
  leadingMobile?: React.ReactNode
  className?: string
}

export const Header = ({ logo, leadingMobile, className }: HeaderProps): JSX.Element => {
  const { isAuthenticated, isLoading } = useAuth()

  return (
    <header
      className={cn(
        'h-14 bg-card border-b border-border sticky top-0 z-40 flex items-center px-4 gap-3',
        className,
      )}
      role="banner"
    >
      {/* ── Left zone ──────────────────────────────────────────── */}
      <div className="flex items-center gap-3 shrink-0">
        {leadingMobile}
        {logo ?? <BrandLockup size="md" />}
        {isAuthenticated && (
          <>
            <div className="hidden md:block h-6 w-px bg-border" aria-hidden="true" />
            <TenantBadge />
          </>
        )}
      </div>

      {/* ── Center zone ─────────────────────────────────────────── */}
      {/* The global search trigger is hidden here (not yet functional) — the
          flex-1 spacer keeps the right-hand actions cluster pinned to the
          right edge at every width regardless. */}
      <div className="flex-1 min-w-0" />

      {/* ── Right zone — actions cluster ───────────────────────── */}
      <div className="flex items-center gap-1.5 shrink-0">
        {isLoading ? (
          <span className="text-sm text-muted-foreground">Loading…</span>
        ) : isAuthenticated ? (
          <>
            <CreateMenu className="hidden md:inline-flex" />
            <NotificationBell />
            <Link
              to="/help"
              aria-label="Help"
              className="hidden md:inline-flex w-9 h-9 rounded items-center justify-center text-muted-foreground hover:bg-muted transition-colors duration-fast"
            >
              <HelpCircle className="h-[18px] w-[18px]" aria-hidden="true" />
            </Link>
            <UserMenuDropdown />
          </>
        ) : (
          <LoginButton size="sm" />
        )}
      </div>
    </header>
  )
}
